import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { useTranslation } from 'react-i18next'

import i18n, { initI18n } from '../i18n/config'

import { applyConsumptionOrigin, captureConsumptionOrigin } from './Main/ResourceSpendFlyLayer'
import { highlightCost } from './modalCostHighlight'
import TailwindModal from './ui/TailwindModal'

// Shared palette first — confirm-modal.css reads its tokens from :root.
import './modalTheme.css'
import './confirm-modal.css'

/** Must match the exit animation duration in confirm-modal.css. */
const EXIT_MS = 140

/**
 * A `t()` call made before the locale bundle is in memory returns the key
 * itself. Callers pass those strings straight through as props, and a truthy
 * raw key defeats every `prop || t(...)` fallback — that is how
 * `MODALS.CONFIRMMODAL.DEFAULTCANCEL` reached a live button. Treat anything
 * that still looks like a dotted i18n key as "not translated yet".
 */
const isUnresolvedKey = (value: string | undefined): boolean =>
  typeof value === 'string' && /^[a-z][\w-]*(\.[\w-]+){1,}$/i.test(value.trim())

/** `prop` unless it is absent or an unresolved key, in which case `fallback()`. */
const resolveLabel = (prop: string | undefined, fallback: () => string): string => {
  if (prop && !isUnresolvedKey(prop)) return prop
  return fallback()
}

/**
 * `useTranslation` only re-renders on `languageChanged`. This root can mount
 * before `initI18n()` resolves, and adding the `en` bundle to an already-mounted
 * tree fires `initialized` / `loaded` / `added` instead — without this the modal
 * keeps rendering the raw keys it read on its first pass.
 */
function useI18nReadyRerender(): void {
  const [, force] = useState(0)
  useEffect(() => {
    if (i18n.isInitialized && i18n.hasResourceBundle(i18n.language, 'translation')) return
    const bump = () => force((n) => n + 1)
    i18n.on('initialized', bump)
    i18n.on('loaded', bump)
    i18n.on('added', bump)
    return () => {
      i18n.off('initialized', bump)
      i18n.off('loaded', bump)
      i18n.off('added', bump)
    }
  }, [])
}

/**
 * The diamond rule. The comp carries two of these, both centred and the same
 * width (~31% of the modal): one straddling the header divider, one above the
 * buttons.
 */
const Ornament: React.FC<{ modifier?: string }> = ({ modifier }) => (
  <div
    className={`confirm-modal__ornament${modifier ? ` confirm-modal__ornament--${modifier}` : ''}`}
    aria-hidden="true"
  >
    <span className="confirm-modal__ornament-line" />
    <span className="confirm-modal__ornament-center">
      <i className="confirm-modal__ornament-dot confirm-modal__ornament-dot--left" />
      <i className="confirm-modal__ornament-diamond confirm-modal__ornament-diamond--left-outer" />
      <i className="confirm-modal__ornament-diamond confirm-modal__ornament-diamond--left-inner" />
      <i className="confirm-modal__ornament-diamond confirm-modal__ornament-diamond--main" />
      <i className="confirm-modal__ornament-diamond confirm-modal__ornament-diamond--right-inner" />
      <i className="confirm-modal__ornament-diamond confirm-modal__ornament-diamond--right-outer" />
      <i className="confirm-modal__ornament-dot confirm-modal__ornament-dot--right" />
    </span>
    <span className="confirm-modal__ornament-line" />
  </div>
)

interface ConfirmModalProps {
  open: boolean
  onClose: () => void
  onConfirm?: () => void
  title?: string
  /** Plain-text body. Omit when passing `content`. */
  message?: string
  /**
   * Rich body, for when a plain string is not enough — building info guides
   * carry styled spans. Takes precedence over `message`.
   */
  content?: React.ReactNode
  /**
   * 'info' is a read-only variant: no action buttons and no ornament rules,
   * just a titled sheet the player dismisses with the close button. Everything
   * else — frame, parchment, header, motion — is shared with 'confirm', which
   * is the whole point of reusing this rather than a bespoke modal.
   */
  variant?: 'confirm' | 'info'
  confirmText?: string
  cancelText?: string
  /**
   * When the cancel plate is a real action (e.g. Decline a request) rather than
   * a dismissal. The header close and backdrop still call `onClose`.
   */
  onCancel?: () => void
  zIndex?: number
  isProcessing?: boolean
  secondaryText?: string
  onSecondary?: () => void
  showCancelButton?: boolean
  closeOnBackdropClick?: boolean
  disableEscapeKeyDown?: boolean
  /** Stamped as data-ftue-anchor on the confirm button so FTUE spotlights can target it. */
  confirmButtonFtueAnchor?: string
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  content,
  variant = 'confirm',
  confirmText,
  cancelText,
  onCancel,
  zIndex,
  isProcessing = false,
  secondaryText,
  onSecondary,
  showCancelButton = true,
  closeOnBackdropClick,
  disableEscapeKeyDown,
  confirmButtonFtueAnchor,
}) => {
  const { t } = useTranslation()
  useI18nReadyRerender()
  const defaultTitle = resolveLabel(title, () => t('modals.confirmModal.defaultTitle'))
  const defaultConfirmText = resolveLabel(confirmText, () =>
    t('modals.confirmModal.defaultConfirm'),
  )
  const defaultCancelText = resolveLabel(cancelText, () => t('modals.confirmModal.defaultCancel'))
  const isInfo = variant === 'info'

  return (
    <TailwindModal
      open={open}
      onClose={onClose}
      zIndex={typeof zIndex === 'number' ? zIndex : 20_000}
      exitMs={EXIT_MS}
      closeOnBackdropClick={closeOnBackdropClick ?? !isProcessing}
      disableEscapeKeyDown={disableEscapeKeyDown ?? isProcessing}
      aria-labelledby="confirm-modal-title"
      aria-describedby="confirm-modal-message"
      backdropClassName="confirm-modal-backdrop"
    >
      <section
        className={`confirm-modal${isInfo ? ' confirm-modal--info' : ''}`}
        role="dialog"
        aria-modal="true"
      >
        <header className="confirm-modal__header">
          <h2 id="confirm-modal-title" className="confirm-modal__title">
            {defaultTitle}
          </h2>

          {!isProcessing && (
            <button
              className="confirm-modal__close"
              type="button"
              aria-label="Close"
              onClick={onClose}
            />
          )}
        </header>

        {/* No decoration in the info variant — it is a page of text, not a
            decision, and the rules only crowd it. */}
        {!isInfo && <Ornament modifier="top" />}

        <div className="confirm-modal__content">
          {content ? (
            <div id="confirm-modal-message" className="confirm-modal__info-body">
              {content}
            </div>
          ) : (
            <p id="confirm-modal-message" className="confirm-modal__message">
              {highlightCost(message ?? '', 'confirm-modal__cost')}
            </p>
          )}

          {isProcessing && (
            <div className="confirm-modal__processing" aria-live="polite">
              <span
                className="confirm-modal__spinner"
                role="status"
                aria-label={t('common.loading')}
              />
              <span className="confirm-modal__processing-text">{t('common.loading')}</span>
            </div>
          )}
        </div>

        {!isInfo && <Ornament />}

        {/* Read-only: nothing to confirm, nothing to cancel. The close button
            in the header is the way out. */}
        {!isInfo && (
          <footer className="confirm-modal__actions">
            <button
              className="confirm-modal__button confirm-modal__button--confirm"
              type="button"
              disabled={isProcessing}
              onClick={onConfirm}
              data-ftue-anchor={confirmButtonFtueAnchor}
              autoFocus
            >
              {defaultConfirmText}
            </button>

            {onSecondary && (
              <button
                className="confirm-modal__button confirm-modal__button--secondary"
                type="button"
                disabled={isProcessing}
                onClick={onSecondary}
              >
                {secondaryText || defaultConfirmText}
              </button>
            )}

            {showCancelButton && (
              <button
                className="confirm-modal__button confirm-modal__button--cancel"
                type="button"
                disabled={isProcessing}
                onClick={onCancel ?? onClose}
              >
                {defaultCancelText}
              </button>
            )}
          </footer>
        )}
      </section>
    </TailwindModal>
  )
}

export default ConfirmModal

export type ConfirmOpenOptions = {
  title?: string
  message?: string
  /** Rich body (e.g. a per-item breakdown). Takes precedence over `message`. */
  content?: React.ReactNode
  confirmText?: string
  cancelText?: string
  onConfirm?: () => void | Promise<void>
  zIndex?: number
}

let confirmOpenImpl: ((options?: ConfirmOpenOptions) => void) | undefined
let confirmRegisterImpl: ((cb: () => void) => void) | undefined
let confirmCloseImpl: (() => void) | undefined

const ConfirmController: React.FC = () => {
  const [visible, setVisible] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false)
  // Undefined means "no caller override" — ConfirmModal resolves the default
  // label at render time. Storing a t() result here would freeze whatever the
  // bundle said at mount, and this root mounts before i18next is initialized
  // (see the createRoot side effect at the bottom of this file), so the frozen
  // value would be the raw key.
  const [title, setTitle] = useState<string | undefined>(undefined)
  const [message, setMessage] = useState<string>('')
  const [content, setContent] = useState<React.ReactNode>(undefined)
  const [confirmText, setConfirmText] = useState<string | undefined>(undefined)
  const [cancelText, setCancelText] = useState<string | undefined>(undefined)
  const [modalZIndex, setModalZIndex] = useState<number>(20_000)

  const onConfirmRef = useRef<(() => void | Promise<void>) | undefined>()
  const isProcessingRef = useRef(false)
  const confirmOriginRef = useRef<{ x: number; y: number } | null>(null)

  useEffect(() => {
    isProcessingRef.current = isProcessing
  }, [isProcessing])

  const close = useCallback(() => {
    if (isProcessingRef.current) return
    setVisible(false)
  }, [])

  const runConfirm = async () => {
    const fn = onConfirmRef.current

    if (!fn) {
      setVisible(false)
      return
    }

    applyConsumptionOrigin(confirmOriginRef.current)

    let result: void | Promise<void>

    try {
      result = fn()
    } catch {
      setVisible(false)
      return
    }

    const isPromise = result != null && typeof (result as Promise<void>).then === 'function'

    if (isPromise) {
      isProcessingRef.current = true
      setIsProcessing(true)

      try {
        await result
      } catch (error) {
        console.error(error)
      } finally {
        isProcessingRef.current = false
        setIsProcessing(false)
      }
    }

    setVisible(false)
  }

  useLayoutEffect(() => {
    confirmOpenImpl = (options?: ConfirmOpenOptions) => {
      const opts = options || {}

      confirmOriginRef.current = captureConsumptionOrigin()
      setTitle(opts.title)
      setMessage(opts.message !== undefined ? opts.message : '')
      setContent(opts.content)
      setConfirmText(opts.confirmText)
      setCancelText(opts.cancelText)
      setModalZIndex(opts.zIndex !== undefined ? opts.zIndex : 20_000)

      if (opts.onConfirm) onConfirmRef.current = opts.onConfirm

      isProcessingRef.current = false
      setIsProcessing(false)
      setVisible(true)
    }

    confirmRegisterImpl = (cb: () => void) => {
      onConfirmRef.current = cb
    }

    confirmCloseImpl = close
    flushPendingConfirmCalls()
  }, [close])

  return (
    <ConfirmModal
      open={visible}
      onClose={close}
      onConfirm={() => void runConfirm()}
      title={title}
      message={message}
      content={content}
      confirmText={confirmText}
      cancelText={cancelText}
      zIndex={modalZIndex}
      isProcessing={isProcessing}
    />
  )
}

const confirmRoot = document.createElement('div')
document.body.appendChild(confirmRoot)
const confirmRootInstance = createRoot(confirmRoot)
// Mount behind i18n, exactly like the app root in index.tsx. This root is a
// module side effect, so without the await it renders the moment ANY module
// imports `confirm` — long before `/locales/en.json` has landed — and every
// t() in the subtree returns its own key.
void initI18n().then(() => {
  confirmRootInstance.render(<ConfirmController />)
})

/**
 * Calls that arrive before the controller mounts (the root now waits on
 * `initI18n`). Dropping them would silently swallow a confirm dialog, so they
 * are replayed in order once the impls are wired up.
 */
let pendingConfirmCalls: Array<() => void> | null = []

function flushPendingConfirmCalls() {
  const queued = pendingConfirmCalls
  pendingConfirmCalls = null
  queued?.forEach((run) => run())
}

export const confirm = {
  open: (options?: ConfirmOpenOptions) => {
    if (!confirmOpenImpl) {
      pendingConfirmCalls?.push(() => confirmOpenImpl?.(options))
      return
    }

    confirmOpenImpl(options)
  },
  onConfirm: (cb: () => void) => {
    if (!confirmRegisterImpl) {
      pendingConfirmCalls?.push(() => confirmRegisterImpl?.(cb))
      return
    }

    confirmRegisterImpl(cb)
  },
  close: () => confirmCloseImpl?.(),
}
